import React, { useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client/react';
import Modal from 'react-bootstrap/Modal';
import { GET_CARRIER_SET_INACTIVE } from '../../../graphql/client/queries';
import { GetAllCarriers_getAllCarriers as Carrier, getDriverCanBeSetInactive } from '../../../types/schemaTypes';
import { CustomButton } from '../../_common';
import SearchableDropdown from '../../_common/SearchableDropdown/SearchableDropdown';
import { Textfield } from '../../_common/Textfield/Textfield';
import { ModalForm } from '../interface';
import './AddEditCarrierModal.scss';
import { useAuthContext } from '../../Auth/AuthProvider';
import { PermissionEnum } from '../../../types/PermissionEnum';
interface Option {
    id: number | string;
    name: string | number;
}

interface AddEditCarrierModalProps {
    modalForm: ModalForm;
    options: Option[];
    selectedCarrier?: Carrier;
    onClickHide: () => void;
    onClickSave: (
        id: number,
        companyName: string,
        name: string,
        stateId: number,
        city: string,
        email: string,
        addressLine1: string,
        addressLine2: string,
        phoneNumber: string,
        type: string,
        zip: string,
        activeInactiveDate: string,
        activeInactiveUser: string,
        extSystemIdentifier: string,
        extSystemName: string,
        isActive: boolean,
        notes: string,
        extSystemIdentifier2: string,
        acronym: string,
    ) => void;
    show: boolean;
    errorMessage?: string;
}
const AddEditCarrierModal = ({
    show,
    options,
    onClickHide,
    onClickSave,
    modalForm,
    selectedCarrier,
    errorMessage,
}: AddEditCarrierModalProps): React.ReactElement => {
    const [selectedId, setSelectedId] = useState(0);
    const [selectedCmpName, setSelectedCmpName] = useState('');
    const [selectedType, setSelectedType] = useState('');
    const [selectedActive, setSelectedActive] = useState(false);
    const [selectedState, setSelectedState] = useState(0);
    const [selectedJDE, setSelectedJDE] = useState('');
    const [selectedJDEName, setSelectedJDEName] = useState('');
    const [selectedAddr1, setSelectedAddr1] = useState('');
    const [selectedAddr2, setSelectedAddr2] = useState('');
    const [selectedInactiveDate, setSelectedInactiveDate] = useState('');
    const [selectedInactiveUser, setSelectedInactiveUser] = useState('');
    const [selectedName, setSelectedName] = useState('');
    const [selectedNotes, setSelectedNotes] = useState('');
    const [selectedPhone, setSelectedPhone] = useState('');
    const [selectedZip, setSelectedZip] = useState('');
    const [selectedEmail, setSelectedEmail] = useState('');
    const [selectedCity, setSelectedCity] = useState('');
    const [selectedISNET, setSelectedISNET] = useState('');
    const [selectedAcronym, setSelectedAcronym] = useState('');

    const isEditForm = modalForm === ModalForm.EDIT_CARRIER;
    const [formErrors, setFormErrrors]: any = useState({});

    const canEditCarrier = useAuthContext().userHasPermission(PermissionEnum.EditDriverCompany);
    const canAddEditLogisticsPartner = useAuthContext().userHasPermission(PermissionEnum.EditLogisticsPartner);

    const [
        getDriverCanBeSetInactive,
        { data: activeDrivers, loading: activeDriversLoading },
    ] = useLazyQuery<getDriverCanBeSetInactive>(GET_CARRIER_SET_INACTIVE, { fetchPolicy: 'network-only' });

    const clearState = (): void => {
        setSelectedId(0);
        //setSelectedActive(false);
        setSelectedName('');
        setSelectedCmpName('');
        setSelectedType('');
        setSelectedJDEName('');
        setSelectedJDE('');
        setSelectedAddr1('');
        setSelectedAddr2('');
        setSelectedInactiveDate('');
        setSelectedInactiveUser('');
        setSelectedNotes('');
        setSelectedEmail('');
        setSelectedCity('');
        setSelectedPhone('');
        setSelectedState(0);
        setSelectedZip('');
        setSelectedISNET('');
        setSelectedAcronym('');
        setFormErrrors({});
    };

    useEffect(() => {
        if (show && selectedCarrier) {
            setSelectedId(selectedCarrier.id ? selectedCarrier.id : 0);
            setSelectedName(selectedCarrier.name ? selectedCarrier.name : '');
            setSelectedCmpName(selectedCarrier.companyName ? selectedCarrier.companyName : '');
            setSelectedType(selectedCarrier.type ? selectedCarrier.type : '');
            setSelectedState(selectedCarrier.stateId ? selectedCarrier.stateId : 0);
            setSelectedActive(selectedCarrier.isActive ? selectedCarrier.isActive : false);
            setSelectedJDE(selectedCarrier.extSystemIdentifier ? selectedCarrier.extSystemIdentifier : '');
            setSelectedJDEName(selectedCarrier.extSystemName ? selectedCarrier.extSystemName : '');
            setSelectedAddr1(selectedCarrier.addressLine1 ? selectedCarrier.addressLine1 : '');
            setSelectedAddr2(selectedCarrier.addressLine2 ? selectedCarrier.addressLine2 : '');
            setSelectedInactiveDate(selectedCarrier.activeInactiveDate ? selectedCarrier.activeInactiveDate : '');
            setSelectedInactiveUser(selectedCarrier.activeInactiveUser ? selectedCarrier.activeInactiveUser : '');
            setSelectedCity(selectedCarrier.city ? selectedCarrier.city : '');
            setSelectedNotes(selectedCarrier.notes ? selectedCarrier.notes : '');
            setSelectedEmail(selectedCarrier.email ? selectedCarrier.email : '');
            setSelectedPhone(selectedCarrier.phoneNumber ? selectedCarrier.phoneNumber : '');
            setSelectedZip(selectedCarrier.zip ? selectedCarrier.zip : '');
            setSelectedISNET(selectedCarrier.extSystemIdentifier2 ? selectedCarrier.extSystemIdentifier2 : '');
            setSelectedAcronym(selectedCarrier.acronym ? selectedCarrier.acronym : '');
        }

        if (show && !selectedCarrier) {
            clearState();
        }
    }, [selectedCarrier, show]);

    useEffect(() => {
        if (!activeDriversLoading) {
            if (activeDrivers && activeDrivers.getDriverCanBeSetInactive) {
                if (!activeDrivers.getDriverCanBeSetInactive.canBeSetInactive) {
                    alert(
                        'There are active loads associated to this carrier. Please deliver the load before you can unassign.',
                    );
                    setSelectedActive(true);
                } else setSelectedActive(false);
            }
        }
    }, [activeDrivers]);

    const items: Option[] = [];
    const LPitems: Option[] = [];
    items.push({ id: 1, name: 'SBX' });
    items.push({ id: 2, name: 'CP' });
    items.push({ id: 3, name: 'OO' });
    LPitems.push({ id: 1, name: 'LP' });

    const onSave = (): void => {
        onClickSave(
            selectedId,
            selectedCmpName,
            selectedName,
            selectedState,
            selectedCity,
            selectedEmail,
            selectedAddr1,
            selectedAddr2,
            selectedPhone,
            selectedType,
            selectedZip,
            selectedInactiveDate,
            selectedInactiveUser,
            selectedJDE,
            selectedJDEName,
            selectedActive,
            selectedNotes,
            selectedISNET.trimStart().trimEnd(),
            selectedAcronym.trimStart().trimEnd(),
        );
        // clearState();
    };

    const onCancel = (): void => {
        clearState();
        onClickHide();
    };

    const checkCarrierCanMadeInactive = (): void => {
        getDriverCanBeSetInactive({ variables: { carrierId: selectedId } });
    };
    const changeCmpName = (event: any): void => {
        setSelectedCmpName(event.target.value);
    };
    const changeName = (event: any): void => {
        setSelectedName(event.target.value);
    };

    const changeAddr1 = (event: any): void => {
        const address = event.target.value ? event.target.value : '';

        setSelectedAddr1(address);
    };
    const changeAddr2 = (event: any): void => {
        const address = event.target.value ? event.target.value : '';

        setSelectedAddr2(address);
    };
    const changeCity = (event: any): void => {
        const city = event.target.value ? event.target.value : '';

        setSelectedCity(city);
    };
    const changeEmail = (event: any): void => {
        const email = event.target.value ? event.target.value : '';

        setSelectedEmail(email);
    };
    const changeZip = (event: any): void => {
        const zip = event.target.value ? event.target.value : '';

        setSelectedZip(zip);
    };
    const getStateName = (): string | number | null => {
        const id = selectedCarrier && selectedCarrier.stateId ? selectedCarrier.stateId.toString() : '';

        if (id == '') return null;

        const numId = parseInt(id);
        const filteredRole = options.filter((item: Option) => item.id === numId);
        const role = filteredRole[0].name;

        return role;
    };

    const changeState = (value: any): void => {
        const id = parseInt(value);
        setSelectedState(id);
    };
    const changeJDE = (event: any): void => {
        const jde = event.target.value ? event.target.value : '';
        setSelectedJDE(jde);
    };

    const changeISNET = (event: any): void => {
        const isnet = event.target.value ? event.target.value : '';
        setSelectedISNET(isnet);
    };

    const changeAcronym = (event: any): void => {
        const acronym = event.target.value ? event.target.value : '';
        setSelectedAcronym(acronym);
    };

    const validatePhoneNumber = (value: any): void => {
        setFormErrrors({});
        const errors = {};
        const phoneRegex = /^[0-9]{3}-[0-9]{3}-[0-9]{4}$/;
        const isCellPhoneValid = phoneRegex.test(value);
        if (!isCellPhoneValid) {
            errors['phoneError'] = 'This field should only contain numeric values.';
        } else if (value && value.length > 12) {
            errors['phoneError'] = 'The number of characters must not exceed 12 digits.';
        }
        setFormErrrors(errors);
        return;
    };

    const changePhone = (event: any): void => {
        const phone = event.target.value ? event.target.value : '';
        if (phone.length == 12) {
            validatePhoneNumber(phone);
        }
        if (phone.length == 0) {
            setFormErrrors({});
        }
        setSelectedPhone(phone);
    };
    const changeType = (eventkey: any): void => {
        ///onst type = event.target.value ? event.target.value : '';
        if (eventkey == '') return;

        const numId = parseInt(eventkey);
        const filteredType = items.filter((item: Option) => item.id === numId);
        const type = filteredType[0].name.toString();
        setSelectedType(type);
    };

    const changeCarrierType = (eventkey: any): void => {
        ///onst type = event.target.value ? event.target.value : '';
        if (eventkey == '') return;

        const numId = parseInt(eventkey);
        const filteredType = LPitems.filter((item: Option) => item.id === numId);
        const type = filteredType[0].name.toString();
        setSelectedType(type);
    };

    const changeStatus = (event: any): void => {
        const boolValue = JSON.parse(event.target.value);
        if (boolValue) setSelectedActive(boolValue);
        else checkCarrierCanMadeInactive();
    };
    const validateForm = () => {
        const errors = {};
        if (selectedPhone.length > 0 && selectedPhone.length < 12) {
            errors['phoneError'] = 'Phone number be 10 digits';
        }

        if (!selectedCmpName || (selectedCarrier !== undefined && !selectedCarrier.companyName)) {
            errors['CmpName'] = 'Carrier Name is required';
        }

        // if (!selectedJDE || (selectedCarrier !== undefined && !selectedCarrier.extSystemIdentifier)) {
        //     errors['extSystemIdentifier'] = 'extSystemIdentifier is required';
        // }
        if (!selectedType || (selectedCarrier !== undefined && !selectedCarrier.type)) {
            errors['type'] = 'Type is required';
        }
        if (!selectedAcronym || (selectedCarrier !== undefined && !selectedCarrier.acronym)) {
            errors['acronym'] = 'Carrier Acronym is required';
        }
        return {
            isValid: Object.keys(errors).length === 0,
            errors,
        };
    };

    const handleSaveChanges = () => {
        setFormErrrors({});
        const { isValid, errors } = validateForm();

        if (!isValid) {
            setFormErrrors(errors);
            return;
        }
        onSave();
    };

    return (
        <Modal show={show} onHide={onClickHide} backdrop="static" size="lg">
            <Modal.Header className="AddEditCarrierModal-Header">
                <Modal.Title className="AddEditCarrierModal-Header-Container">
                    <button className="material-icons AddEditCarrierModal-X-Button" onClick={onClickHide}>
                        close
                    </button>
                    <div className="AddEditCarrierModal-Title">
                        {isEditForm ? 'Edit Carrier' : 'Create New Carrier'}
                    </div>
                    <div className="AddEditCarrierModal-ErrorMessage">{errorMessage}</div>
                </Modal.Title>
            </Modal.Header>
            <div className="AddEditCarrierModal-Upper-TextField-Container">
                <div className="AddEditCarrierModal-CmpName">
                    <Textfield
                        label="*Name"
                        value={selectedCarrier !== undefined ? selectedCarrier.companyName : ''}
                        onChange={(event): void => changeCmpName(event)}
                        onBlur={() => {}}
                        error={formErrors.CmpName}
                        helperText={formErrors.CmpName}
                    />
                </div>
                {canAddEditLogisticsPartner ?          
    (<div className="AddEditCarrierModal-DropDown">
                    <SearchableDropdown
                        dropdownOptions={LPitems}
                        id="Type"
                        label="Type"
                        onSelect={(eventKey: string | null) => {
                            changeCarrierType(eventKey);
                        }}
                        value={selectedCarrier !== undefined ? selectedCarrier.type : ''}
                        errorMessage={formErrors.type}
                        hasError={formErrors.type != undefined}
                    />
                </div>) :
                (<div className="AddEditCarrierModal-DropDown">
                    <SearchableDropdown
                        dropdownOptions={items}
                        id="Type"
                        label="*Type"
                        onSelect={(eventKey: string | null) => {
                            changeType(eventKey);
                        }}
                        value={selectedCarrier !== undefined ? selectedCarrier.type : ''}
                        errorMessage={formErrors.type}
                        hasError={formErrors.type != undefined}
                    />
                </div>)}
                <div className="AddEditCarrierModal-Textfield">
                    <Textfield
                        label="JDENumber"
                        value={selectedCarrier !== undefined ? selectedCarrier.extSystemIdentifier : ''}
                        onChange={(event): void => changeJDE(event)}
                        onBlur={() => {}}
                        // error={formErrors.extSystemIdentifier}
                        // helperText={formErrors.extSystemIdentifier}
                    />
                </div>
                <div className="AddEditCarrierModal-Textfield">
                    <Textfield
                        label="ISNET ID"
                        value={selectedCarrier !== undefined ? selectedCarrier.extSystemIdentifier2 : ''}
                        onChange={(event): void => changeISNET(event)}
                        onBlur={() => {}}
                        // error={formErrors.extSystemIdentifier}
                        // helperText={formErrors.extSystemIdentifier}
                    />
                </div>
                <div className="AddEditCarrierModal-Textfield">
                    <Textfield
                        label="*Carrier Acronym"
                        value={selectedCarrier !== undefined ? selectedCarrier.acronym : ''}
                        onChange={(event): void => changeAcronym(event)}
                        onBlur={() => {}}
                        maxLength={10}
                        error={formErrors.acronym}
                        helperText={formErrors.acronym}
                    />
                </div>
            </div>
            {selectedCarrier?.type != 'SBX' && (
                <div className="AddEditCarrierModal-HasAccess-Container">
                    <div className="AddEditCarrierModal-HasAccess-Title">Is Carrier Active?</div>
                    <div className="AddEditCarrierModal-HasAccess-Radio-Container">
                        <div className="AddEditCarrierModal-Radio">
                            <input
                                className="AddEditCarrierModal-Radio-Input"
                                type="radio"
                                value="true"
                                checked={selectedActive}
                                onChange={(event): void => changeStatus(event)}
                            />
                            Yes
                        </div>
                        <div>
                            <input
                                className="AddEditCarrierModal-Radio-Input"
                                type="radio"
                                value="false"
                                checked={!selectedActive}
                                onChange={(event): void => changeStatus(event)}
                            />
                            No
                        </div>
                    </div>
                </div>
            )}
            <div className="AddEditCarrierModal-Contact">Contact Details</div>
            <div className="AddEditCarrierModal-Upper-TextField-Container">
                <div className="AddEditCarrierModal-Textfield">
                    <Textfield
                        label="Name"
                        value={selectedCarrier !== undefined ? selectedCarrier.name : ''}
                        onChange={(event): void => changeName(event)}
                        onBlur={() => {}}
                    />
                </div>
                <div className="AddEditCarrierModal-Textfield">
                    <Textfield
                        label="PhoneNumber"
                        value={selectedCarrier !== undefined ? selectedCarrier.phoneNumber : ''}
                        onChange={event => {
                            changePhone(event);
                        }}
                        mask={'999-999-9999'}
                        maxLength={12}
                        error={formErrors.phoneError}
                        helperText={formErrors.phoneError}
                        onBlur={() => {}}
                    />
                </div>
                <div className="AddEditCarrierModal-Textfield">
                    <Textfield
                        label="Email"
                        value={selectedCarrier !== undefined ? selectedCarrier.email : ''}
                        onChange={(event): void => changeEmail(event)}
                        onBlur={() => {}}
                    />
                </div>
            </div>
            <div className="AddEditCarrierModal-Upper-TextField-Container">
                <div className="AddEditCarrierModal-Textfield">
                    <Textfield
                        label="Address Line1"
                        value={selectedCarrier !== undefined ? selectedCarrier.addressLine1 : ''}
                        onChange={(event): void => changeAddr1(event)}
                        onBlur={() => {}}
                    />
                </div>
                <div className="AddEditCarrierModal-Textfield">
                    <Textfield
                        label="Address Line2"
                        value={selectedCarrier !== undefined ? selectedCarrier.addressLine2 : ''}
                        onChange={(event): void => changeAddr2(event)}
                        onBlur={() => {}}
                    />
                </div>
                <div className="AddEditCarrierModal-Textfield">
                    <Textfield
                        label="City"
                        value={selectedCarrier !== undefined ? selectedCarrier.city : ''}
                        onChange={(event): void => changeCity(event)}
                        onBlur={() => {}}
                    />
                </div>
            </div>
            <div className="AddEditCarrierModal-Upper-TextField-Container">
                <div className="AddEditCarrierModal-Textfield">
                    <Textfield
                        label="Zip"
                        value={selectedCarrier !== undefined ? selectedCarrier.zip : ''}
                        onChange={(event): void => changeZip(event)}
                        onBlur={() => {}}
                    />
                </div>
                <div className="AddEditCarrierModal-Textfield">
                    <SearchableDropdown
                        dropdownOptions={options}
                        id="state"
                        label="State"
                        onSelect={(eventKey: string | null) => {
                            changeState(eventKey);
                        }}
                        value={getStateName()}
                    />
                </div>
            </div>
            <Modal.Footer className="AddEditCarrierModal-Footer">
                <CustomButton title="Cancel" isBordered onClick={onCancel}></CustomButton>
                <CustomButton
                    title="Save"
                    //isDisabled={setSaveButtonStatus()}
                    onClick={handleSaveChanges}
                ></CustomButton>
            </Modal.Footer>
        </Modal>
    );
};

export default AddEditCarrierModal;

